import React from "react";
import { useNavigate } from 'react-router-dom';
import { signIn, signOut } from 'aws-amplify/auth'
import { fetchAuthSession } from 'aws-amplify/auth'
import { bootIntercom } from '../utils/intercom';
import { fetchUserAttributes, confirmSignIn, resetPassword, confirmResetPassword, updateUserAttributes, confirmUserAttribute, sendUserAttributeVerificationCode, updateMFAPreference, setUpTOTP, verifyTOTPSetup, rememberDevice, associateWebAuthnCredential } from 'aws-amplify/auth';
import { usePostHog } from 'posthog-js/react';
import { getOrgConfig } from '../utils/api';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import App from '../App';
import { toast } from 'react-hot-toast';
import brandMark from '../assets/img/Icon-Transparent.png';
import LoginBrand from './LoginBrand';
import './Login.css';
import { useTheme } from '../context/ThemeContext';
import { applyUiPreferences, markShellEntrance, shellFromPreferences } from '../utils/uiPreferences';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { QRCodeSVG } from 'qrcode.react';
import { useOptimisticNotifications } from '../hooks/useOptimisticNotifications';
import { setPasskeyMfaEnabled } from '../utils/passkeyMfaAuth';
import { consumeDeepLink } from '../utils/deepLink';
import { clearInviteToken, getInviteToken } from '../utils/inviteToken';
import InviteWelcome, { requestNewInvite, useInviteCheck } from './InviteWelcome';

// How long a remembered device is trusted before we require MFA again.
// Cognito has no server-side TTL on remembered devices, so we enforce one
// here: past the window we drop the local device keys before sign-in, which
// makes Cognito treat this as a new device and issue a fresh MFA challenge.
const DEVICE_TRUST_DAYS = 30;
const DEVICE_KEY_RE = /\.(deviceKey|deviceGroupKey|randomPasswordKey)$/;

function purgeExpiredRememberedDevice() {
  try {
    const rememberedAt = Number(localStorage.getItem('mfaDeviceRememberedAt'));
    if (!rememberedAt) return;
    if (Date.now() - rememberedAt < DEVICE_TRUST_DAYS * 24 * 60 * 60 * 1000) return;
    Object.keys(localStorage)
      .filter((key) => key.startsWith('CognitoIdentityServiceProvider.') && DEVICE_KEY_RE.test(key))
      .forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem('mfaDeviceRememberedAt');
  } catch {
    // localStorage unavailable — nothing to purge
  }
}

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [newPasswordRequired, setNewPasswordRequired] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const { fetchSettings, userSettings } = useUserSettingsContext();
  const posthog = usePostHog();
  const notify = useOptimisticNotifications();
  const theme = useTheme();

  // Into the app. The user's appearance preferences are applied to
  // ThemeContext first (and html[data-shell] with them), so the shell's first
  // frame is already their layout — a New-look user never sees Classic flash.
  // New-look sign-ins also get the sidebar entrance (InsetLayout).
  const enterApp = (settings) => {
    const prefs = (settings || userSettings)?.uiPreferences;
    applyUiPreferences(theme, prefs);
    if ((shellFromPreferences(prefs) || theme.uiShell) === 'inset') markShellEntrance();
    navigate(consumeDeepLink('/dashboard'));
  };
  const [emailFocus, setEmailFocus] = React.useState(false);
  const [passwordFocus, setPasswordFocus] = React.useState(false);

  // MFA states
  const [mfaRequired, setMfaRequired] = React.useState(false);
  const [mfaCode, setMfaCode] = React.useState('');
  const [phoneSetupRequired, setPhoneSetupRequired] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneVerificationSent, setPhoneVerificationSent] = React.useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = React.useState('');
  const [mfaError, setMfaError] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [mfaMethodSelection, setMfaMethodSelection] = React.useState(false); // Show SMS vs TOTP picker
  const [totpRequired, setTotpRequired] = React.useState(false); // Show TOTP code entry
  const [totpCode, setTotpCode] = React.useState('');
  const [totpSetupRequired, setTotpSetupRequired] = React.useState(false); // Show TOTP QR setup
  const [totpSetupUri, setTotpSetupUri] = React.useState('');
  const [totpSetupCode, setTotpSetupCode] = React.useState('');
  const [rememberThisDevice, setRememberThisDevice] = React.useState(false);
  const [availableMfaMethods, setAvailableMfaMethods] = React.useState([]); // Methods the user has configured

  // Email MFA code entry states (used when user picks EMAIL as their MFA method)
  const [emailOtpSent, setEmailOtpSent] = React.useState(false);
  const [emailOtpCode, setEmailOtpCode] = React.useState('');
  const [emailOtpError, setEmailOtpError] = React.useState('');
  const [passkeyError, setPasskeyError] = React.useState('');
  const [promptPasskeyEnrollmentAfterMfa, setPromptPasskeyEnrollmentAfterMfa] = React.useState(false);
  const [showPasskeyEnrollmentPrompt, setShowPasskeyEnrollmentPrompt] = React.useState(false);
  const [passkeyEnrollmentLoading, setPasskeyEnrollmentLoading] = React.useState(false);
  const [passkeyEnrollmentError, setPasskeyEnrollmentError] = React.useState('');

  // Password visibility states
  const [showPassword, setShowPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  // Email validation
  const [emailError, setEmailError] = React.useState('');

  // Remember me
  const [rememberMe, setRememberMe] = React.useState(false);

  // Forgot password states
  const [forgotPasswordMode, setForgotPasswordMode] = React.useState(false);
  const [confirmationCode, setConfirmationCode] = React.useState('');
  const [resetPasswordStep, setResetPasswordStep] = React.useState('request'); // 'request', 'verify', 'reset'
  const [resetPasswordError, setResetPasswordError] = React.useState('');
  // Where Cognito actually sent the reset code: { medium: 'SMS'|'EMAIL', destination: masked }.
  // With a verified phone the pool's account-recovery setting texts it, not emails.
  const [resetDelivery, setResetDelivery] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false); // Add loading state
  // Welcome-email one-time link (/welcome#t=…): verified, then the invitee
  // picks a password and signs in through the normal flow below.
  const [inviteToken] = React.useState(getInviteToken);
  const [invite, setInvite] = useInviteCheck(inviteToken);
  const [pendingInviteLogin, setPendingInviteLogin] = React.useState(false);

  // Auto-submit 6-digit MFA codes as soon as the full code lands (typed or pasted).
  // The `submitted` refs guard against React StrictMode's double-effect-run and against
  // re-entry while a previous request is still in flight. Refs reset when the input
  // clears (e.g., after a failed verification that empties the field).
  const totpFormRef = React.useRef(null);
  const mfaFormRef = React.useRef(null);
  const emailOtpFormRef = React.useRef(null);
  const totpSubmittedRef = React.useRef(false);
  const mfaSubmittedRef = React.useRef(false);
  const emailOtpSubmittedRef = React.useRef(false);
  React.useEffect(() => {
    if (totpCode.length < 6) { totpSubmittedRef.current = false; return; }
    if (totpSubmittedRef.current || isLoading) return;
    totpSubmittedRef.current = true;
    totpFormRef.current?.requestSubmit();
  }, [totpCode, isLoading]);
  React.useEffect(() => {
    if (mfaCode.length < 6) { mfaSubmittedRef.current = false; return; }
    if (mfaSubmittedRef.current || isLoading) return;
    mfaSubmittedRef.current = true;
    mfaFormRef.current?.requestSubmit();
  }, [mfaCode, isLoading]);
  React.useEffect(() => {
    if (emailOtpCode.length < 6) { emailOtpSubmittedRef.current = false; return; }
    if (emailOtpSubmittedRef.current || isLoading) return;
    emailOtpSubmittedRef.current = true;
    emailOtpFormRef.current?.requestSubmit();
  }, [emailOtpCode, isLoading]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setPasskeyError('');
    setPasskeyEnrollmentError('');
    setPromptPasskeyEnrollmentAfterMfa(false);

    if (newEmail.trim() === '') {
      setEmailError('');
    } else if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: '#EF4444' };
    if (strength <= 3) return { strength, label: 'Fair', color: '#F59E0B' };
    if (strength <= 4) return { strength, label: 'Good', color: '#10B981' };
    return { strength, label: 'Strong', color: '#059669' };
  };

  function login(event) {
    event.preventDefault();
    
    if (newPasswordRequired && newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }
  
    setIsLoading(true); // Set loading true
    // Expire device trust past the 30-day window so MFA is required again
    purgeExpiredRememberedDevice();
    return signIn({
      username: email,
      password: password
    })
      .then(async (user) => {
        switch (user.nextStep.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
            setNewPasswordRequired(true);
            toast('New password required. Please set a new password.');
            return;
          case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
            setAvailableMfaMethods(user.nextStep.allowedMFATypes || ['SMS']);
            setMfaRequired(true);
            toast('A verification code has been sent to your phone.');
            return;
          case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
            setAvailableMfaMethods(user.nextStep.allowedMFATypes || ['TOTP']);
            setTotpRequired(true);
            toast('Enter the code from your authenticator app.');
            return;
          case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
            setMfaMethodSelection(true);
            toast('Please choose how you want to verify your identity.');
            return;
          case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
            setAvailableMfaMethods(user.nextStep.allowedMFATypes || []);
            setMfaMethodSelection(true);
            toast('Choose a verification method.');
            return;
          case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
            setAvailableMfaMethods(user.nextStep.allowedMFATypes || ['EMAIL']);
            setEmailOtpSent(true);
            toast('A sign-in code has been sent to your email.');
            return;
          case 'DONE':
            toast.success('Login successful!');
            break;
          default:
            toast.success('Login successful!');
            break;
        }
        try {
          // Handle remember me
          if (rememberMe) {
            localStorage.setItem('rememberedEmail', email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }

          // First ensure we have a valid session
          await fetchAuthSession();
          // Then fetch user attributes
          const userAttributes = await fetchUserAttributes();
          // Initialize Intercom with user data
          bootIntercom({
            userId: userAttributes.email,
            name: userAttributes.given_name,
            email: userAttributes.email,
          });

          // Identify user in PostHog
          if (posthog && userAttributes.email) {
            // Set person properties for flags before identifying
            posthog.setPersonPropertiesForFlags({
              email: userAttributes.email,
              name: userAttributes.given_name,
            });

            posthog.identify(userAttributes.email, {
              email: userAttributes.email,
              name: userAttributes.given_name,
            });
          }

          // Fetch user settings using email
          const loadedSettings = await fetchSettings();

          enterApp(loadedSettings);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setPassword('');
          toast.error('Error fetching user data. Please try again.', { duration: 5000 });
        }
      })
      .catch(error => {
        switch (error.name) {
          case 'UserAlreadyAuthenticatedException':
            // Refresh the access token and then navigate
            (async () => {
              try {
                const session = await fetchAuthSession();

                toast.success('Session refreshed! Redirecting...');
                enterApp();
              } catch (refreshError) {
                console.error('Error refreshing session:', refreshError);
                setPassword('');
                toast.error('Error refreshing session. Please try again.');
              }
            })();
            break;
          default:
            toast.error(`Error signing in: ${error.message}`);
            console.error('Error signing in:', error);
            break;
        }
      })
      .finally(() => {
        setIsLoading(false); // Set loading false
      });
  }

  async function handleNewPasswordSubmit(event) {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      toast.error('Passwords do not match'); // Notify user about mismatched passwords
      return;
    }

    setIsLoading(true); // Set loading true
    try {
      // Step 1: Sign in with the existing password
      const user = await signIn({
        username: email,
        password: password,
      });

      // Step 2: Check if the user is in the 'NEW_PASSWORD_REQUIRED' state
      if (user.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // Step 3: Respond to the challenge by submitting the new password
        const passwordResult = await confirmSignIn({
          challengeResponse: newPassword,
        });

        // Check if MFA setup is needed after password change
        if (passwordResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          setNewPasswordRequired(false);
          setMfaRequired(true);
          toast.success('Password updated! Please enter the verification code sent to your phone.');
          return;
        }
        if (passwordResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setNewPasswordRequired(false);
          setTotpRequired(true);
          toast.success('Password updated! Enter the code from your authenticator app.');
          return;
        }
        if (passwordResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION') {
          setNewPasswordRequired(false);
          setMfaMethodSelection(true);
          toast.success('Password updated! Please set up two-factor authentication.');
          return;
        }

        // After successful password change, proceed with the normal login flow
        const session = await fetchAuthSession();
        const userAttributes = await fetchUserAttributes();

        // Initialize Intercom
        bootIntercom({
          userId: userAttributes.email,
          name: userAttributes.given_name,
          email: userAttributes.email,
        });
        
        // Identify user in PostHog
        if (posthog && userAttributes.email) {
          // Set person properties for flags before identifying
          posthog.setPersonPropertiesForFlags({
            email: userAttributes.email,
            name: userAttributes.given_name,
          });

          posthog.identify(userAttributes.email, {
            email: userAttributes.email,
            name: userAttributes.given_name,
          });
        }
        
        // Fetch user settings
        const loadedSettings = await fetchSettings();

        // Redirect to the dashboard
        enterApp(loadedSettings);
        toast.success('Password updated successfully! Redirecting to dashboard...');
      }
    } catch (error) {
      console.error('Error confirming new password:', error);

      setPasswordError('The new password is invalid. Please follow password guidelines.');
      toast.error(error.message);
    } finally {
      setIsLoading(false); // Set loading false
    }
  }

  // Function to initiate the forgot password flow
  async function handleForgotPasswordRequest(event) {
    event.preventDefault();
    
    if (!email) {
      setResetPasswordError('Please enter your email address');
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true); // Set loading true
    
    // Optimistically move to next step
    const originalStep = resetPasswordStep;
    setResetPasswordStep('verify');
    setResetPasswordError('');
    
    // An invitee who never set a password can't reset one — Cognito refuses in
    // FORCE_CHANGE_PASSWORD. Send them a fresh setup link instead.
    const resetOrInvite = resetPassword({ username: email }).then((out) => {
      const d = out?.nextStep?.codeDeliveryDetails;
      setResetDelivery(d ? { medium: d.deliveryMedium, destination: d.destination } : null);
      return out;
    }).catch(async (error) => {
      if (error?.name === 'NotAuthorizedException' && /current state/i.test(error?.message || '')) {
        await requestNewInvite({ email });
        setResetPasswordStep('invite');
        return { invite: true };
      }
      throw error;
    });

    try {
      await notify.success(
        'Verification code sent',
        resetOrInvite,
        'Error sending verification code',
        {
          revertAction: () => {
            setResetPasswordStep(originalStep);
          },
          onError: (error) => {
            setResetPasswordError(error.message);
          }
        }
      );
    } catch (error) {
      console.error('Error requesting password reset:', error);
      // Error handling already done by notify.success
    } finally {
      setIsLoading(false); // Set loading false
    }
  }
  
  // Function to verify the confirmation code
  async function handleVerifyCode(event) {
    event.preventDefault();
    
    if (!confirmationCode) {
      setResetPasswordError('Please enter the verification code');
      toast.error('Please enter the verification code');
      return;
    }
    
    // Move to password reset step if code is provided
    setResetPasswordStep('reset');
    setResetPasswordError('');
  }
  
  // Function to confirm the new password with verification code
  async function handleForgotPasswordConfirm(event) {
    event.preventDefault();
    
    if (!newPassword) {
      setResetPasswordError('Please enter a new password');
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setResetPasswordError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true); // Set loading true
    
    // Store original state for potential revert
    const originalState = {
      forgotPasswordMode,
      resetPasswordStep,
      confirmationCode,
      newPassword,
      confirmPassword,
      resetPasswordError
    };
    
    // Optimistically reset to login state
    setForgotPasswordMode(false);
    setResetPasswordStep('request');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordError('');
    
    try {
      await notify.success(
        'Password reset successful! You can now log in with your new password.',
        confirmResetPassword({
          username: email,
          confirmationCode: originalState.confirmationCode,
          newPassword: originalState.newPassword
        }),
        'Error resetting password',
        {
          revertAction: () => {
            setForgotPasswordMode(originalState.forgotPasswordMode);
            setResetPasswordStep(originalState.resetPasswordStep);
            setConfirmationCode(originalState.confirmationCode);
            setNewPassword(originalState.newPassword);
            setConfirmPassword(originalState.confirmPassword);
            setResetPasswordError(originalState.resetPasswordError);
          },
          onError: (error) => {
            setResetPasswordError(error.message);
          }
        }
      );
    } catch (error) {
      console.error('Error confirming password reset:', error);
      // Error handling already done by notify.success
    } finally {
      setIsLoading(false); // Set loading false
    }
  }
  
  // Function to cancel forgot password flow and return to login
  function cancelForgotPassword() {
    setForgotPasswordMode(false);
    setResetPasswordStep('request');
    setConfirmationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordError('');
  }

  // Helper to run the post-login flow (Intercom, PostHog, fetchSettings, navigate)
  async function completeLogin() {
    await fetchAuthSession();
    const userAttributes = await fetchUserAttributes();

    // Remember this device so MFA is skipped on future logins — only when the
    // user opted in via the "Remember this device" checkbox.
    if (rememberThisDevice) {
      try {
        await rememberDevice();
        // Start the 30-day trust window (enforced by purgeExpiredRememberedDevice)
        localStorage.setItem('mfaDeviceRememberedAt', String(Date.now()));
        console.log('[MFA] Device remembered successfully');
      } catch (err) {
        console.error('[MFA] rememberDevice failed:', err);
      }
    }

    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    bootIntercom({
      userId: userAttributes.email,
      name: userAttributes.given_name,
      email: userAttributes.email,
    });

    if (posthog && userAttributes.email) {
      posthog.setPersonPropertiesForFlags({
        email: userAttributes.email,
        name: userAttributes.given_name,
      });
      posthog.identify(userAttributes.email, {
        email: userAttributes.email,
        name: userAttributes.given_name,
      });
    }

    const loadedSettings = await fetchSettings();
    if (promptPasskeyEnrollmentAfterMfa) {
      setShowPasskeyEnrollmentPrompt(true);
      return;
    }

    enterApp(loadedSettings);
  }

  // Handle MFA code submission
  async function handleMfaCodeSubmit(event) {
    event.preventDefault();
    setIsLoading(true);
    setMfaError('');

    try {
      const result = await confirmSignIn({ challengeResponse: mfaCode });

      if (result.nextStep.signInStep === 'DONE') {
        toast.success('Login successful!');
        await completeLogin();
      } else {
        setMfaError('Verification could not be completed. Please sign in again.');
        setMfaCode('');
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      setMfaError('Invalid verification code. Please try again.');
      setMfaCode('');
      toast.error('Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  }

  // Handle phone number submission for MFA setup
  async function handlePhoneSetup(event) {
    event.preventDefault();
    setIsLoading(true);
    setMfaError('');

    if (!phoneNumber) {
      setMfaError('Please enter a phone number.');
      setIsLoading(false);
      return;
    }

    try {
      await updateUserAttributes({
        userAttributes: {
          phone_number: phoneNumber, // PhoneInput already returns E.164 format
        },
      });

      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });

      setPhoneVerificationSent(true);
      toast('A verification code has been sent to your phone.');
    } catch (error) {
      console.error('Error setting phone number:', error);
      setMfaError('Error setting phone number. Please check the format and try again.');
      toast.error('Error setting phone number.');
    } finally {
      setIsLoading(false);
    }
  }

  // Handle phone verification code submission
  async function handlePhoneVerification(event) {
    event.preventDefault();
    setIsLoading(true);
    setMfaError('');

    try {
      await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: phoneVerificationCode,
      });

      await updateMFAPreference({ sms: 'ENABLED' });

      toast.success('Phone verified and MFA enabled!');
      await completeLogin();
    } catch (error) {
      console.error('Phone verification error:', error);
      setMfaError('Invalid verification code. Please try again.');
      setPhoneVerificationCode('');
      toast.error('Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  }

  // Resend the MFA code during login. Cognito has no dedicated "resend MFA" API,
  // so we re-initiate sign-in with the stored credentials and auto-select the same
  // MFA method — Cognito issues a fresh SMS/email code as part of that flow.
  async function handleResendLoginMfa(method) {
    if (resendCooldown > 0) return;
    if (!email || !password) {
      toast.error('Session expired — please sign in again.');
      resetMfaState();
      return;
    }
    try {
      const signInResult = await signIn({ username: email, password });
      if (signInResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        await confirmSignIn({ challengeResponse: method });
      }
      toast('Verification code resent.');
      setMfaCode('');
      setEmailOtpCode('');
      setResendCooldown(30);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error resending MFA code:', error);
      toast.error('Could not resend code. Please try signing in again.');
    }
  }

  // Resend SMS verification code for phone setup
  async function handleResendPhoneCode() {
    if (resendCooldown > 0) return;

    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      toast('Verification code resent.');
      setResendCooldown(30);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error('Error resending code. Please try again.');
    }
  }

  // Handle TOTP code submission during login
  async function handleTotpCodeSubmit(event) {
    event.preventDefault();
    setIsLoading(true);
    setMfaError('');

    try {
      const result = await confirmSignIn({ challengeResponse: totpCode });
      if (result.nextStep.signInStep === 'DONE') {
        toast.success('Login successful!');
        await completeLogin();
      } else {
        setMfaError('Verification could not be completed. Please sign in again.');
        setTotpCode('');
      }
    } catch (error) {
      console.error('TOTP verification error:', error);
      setMfaError('Invalid code. Please try again.');
      setTotpCode('');
      toast.error('Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  }

  // Handle MFA method selection (SMS or TOTP) during setup
  async function handleMfaMethodSelect(method) {
    setIsLoading(true);
    setMfaError('');
    setMfaMethodSelection(false);

    try {
      if (method === 'EMAIL') {
        // Amplify's MFA selection challenge only accepts 'SMS' | 'TOTP' | 'EMAIL'
        // (it maps EMAIL to Cognito's EMAIL_OTP wire value itself).
        const result = await confirmSignIn({ challengeResponse: 'EMAIL' });
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          setEmailOtpSent(true);
          toast('A verification code has been sent to your email.');
        } else {
          setMfaError('Email MFA is not available. Please try another method.');
          setMfaMethodSelection(true);
        }
      } else if (method === 'SMS') {
        const result = await confirmSignIn({ challengeResponse: 'SMS' });
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          setMfaRequired(true);
          toast('A verification code has been sent to your phone.');
        } else {
          setPhoneSetupRequired(true);
          toast('Please add your phone number.');
        }
      } else {
        const result = await confirmSignIn({ challengeResponse: 'TOTP' });
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setTotpRequired(true);
          toast('Enter the code from your authenticator app.');
        } else {
          const totpSetup = await setUpTOTP();
          const uri = totpSetup.getSetupUri('Lodgeic', email);
          setTotpSetupUri(uri.toString());
          setTotpSetupRequired(true);
        }
      }
    } catch (error) {
      console.error('MFA method selection error:', error);
      if (method === 'SMS') {
        setPhoneSetupRequired(true);
        toast('Please add your phone number.');
      } else if (method === 'TOTP') {
        try {
          const totpSetup = await setUpTOTP();
          const uri = totpSetup.getSetupUri('Lodgeic', email);
          setTotpSetupUri(uri.toString());
          setTotpSetupRequired(true);
        } catch (totpErr) {
          setMfaError('Error setting up authenticator. Please try again.');
          toast.error('Error setting up authenticator.');
        }
      } else {
        setMfaError('Error setting up email MFA. Please try another method.');
        setMfaMethodSelection(true);
      }
    } finally {
      setIsLoading(false);
    }
  }

  // Handle TOTP setup verification (scanning QR + entering code)
  async function handleTotpSetupVerify(event) {
    event.preventDefault();
    setIsLoading(true);
    setMfaError('');

    try {
      await verifyTOTPSetup({ code: totpSetupCode });
      await updateMFAPreference({ totp: 'ENABLED' });

      toast.success('Authenticator app verified and MFA enabled!');
      await completeLogin();
    } catch (error) {
      console.error('TOTP setup verification error:', error);
      setMfaError('Invalid code. Make sure you scanned the QR code and entered the current code.');
      setTotpSetupCode('');
      toast.error('Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  }

  function getActiveMfaChallengeContext() {
    if (mfaMethodSelection) return { method: 'SELECTION' };
    if (mfaRequired) return { method: 'SMS' };
    if (totpRequired) return { method: 'TOTP' };
    if (emailOtpSent) return { method: 'EMAIL' };
    return null;
  }

  function showMfaChallengeContext(context) {
    setMfaMethodSelection(context?.method === 'SELECTION');
    setMfaRequired(context?.method === 'SMS');
    setTotpRequired(context?.method === 'TOTP');
    setEmailOtpSent(context?.method === 'EMAIL');
    setPhoneSetupRequired(false);
    setPhoneVerificationSent(false);
    setTotpSetupRequired(false);
  }

  async function restorePasswordMfaChallenge(context) {
    if (!context || !password) return;

    try {
      const signInResult = await signIn({ username: email, password });
      let result = signInResult;
      const allowedMethods = signInResult.nextStep.allowedMFATypes || availableMfaMethods;
      setAvailableMfaMethods(allowedMethods || []);

      if (context.method === 'SELECTION') {
        setMfaCode('');
        setTotpCode('');
        setEmailOtpCode('');
        showMfaChallengeContext(context);
        return;
      }

      if (signInResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        result = await confirmSignIn({ challengeResponse: context.method });
      }

      const step = result.nextStep.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setMfaCode('');
        showMfaChallengeContext({ method: 'SMS' });
        setPasskeyError('Passkey was not available. Enter the new code we sent to your phone.');
        return;
      }

      if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        showMfaChallengeContext({ method: 'TOTP' });
        setPasskeyError('Passkey was not available. Enter your authenticator code to continue.');
        return;
      }

      if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        setEmailOtpCode('');
        showMfaChallengeContext({ method: 'EMAIL' });
        setPasskeyError('Passkey was not available. Enter the new code we sent to your email.');
        return;
      }

      if (step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        showMfaChallengeContext({ method: 'SELECTION' });
      }
    } catch (restoreError) {
      console.error('Error restoring MFA challenge after passkey attempt:', restoreError);
      showMfaChallengeContext(context);
      setPasskeyError('Passkey was not available. Continue with your verification code, or go back to login if the code does not verify.');
    }
  }

  // Sign in with passkey as a complete first-factor — relies on the user pool's
  // WebAuthnConfiguration.FactorConfiguration = MULTI_FACTOR_WITH_USER_VERIFICATION
  // so the passkey assertion satisfies both first-factor and MFA in one ceremony.
  async function handlePasskeySignIn({ enrollAfterMfaOnMissing = false, restoreMfaContext = null } = {}) {
    setIsLoading(true);
    setPasskeyError('');

    try {
      const initResult = await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'WEB_AUTHN',
        },
      });

      let result = initResult;
      if (initResult.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        result = await confirmSignIn({ challengeResponse: 'WEB_AUTHN' });
      }

      if (result.nextStep.signInStep === 'DONE') {
        setPromptPasskeyEnrollmentAfterMfa(false);
        toast.success('Login successful!');
        await completeLogin();
      } else {
        setPasskeyError(`Unexpected sign-in step: ${result.nextStep.signInStep}`);
      }
    } catch (error) {
      console.error('Passkey sign-in error:', error);
      const passkeyErrorMessage = `${error.message || ''} ${error.name || ''}`.toLowerCase();
      if (error.name === 'NotAllowedError') {
        setPasskeyError('Passkey authentication was cancelled.');
      } else if (passkeyErrorMessage.includes('not found') || passkeyErrorMessage.includes('no credential') || passkeyErrorMessage.includes('not registered')) {
        if (enrollAfterMfaOnMissing) {
          setPromptPasskeyEnrollmentAfterMfa(true);
          setPasskeyError('No passkey found. Use your code this time, then we will help you add one.');
          toast('No passkey found. Sign in with your code this time, then add one.');
        } else {
          setPasskeyError('No passkey found for this account. Sign in with your password, then set one up in Settings > Security.');
        }
      } else {
        setPasskeyError(error.message || 'Unable to sign in with passkey. Use your password instead.');
      }
      await restorePasswordMfaChallenge(restoreMfaContext);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleUsePasskeyInstead() {
    if (!email || !validateEmail(email)) {
      setMfaError('Enter a valid email address to use passkey sign-in.');
      return;
    }

    setMfaError('');
    setEmailOtpError('');
    await handlePasskeySignIn({
      enrollAfterMfaOnMissing: true,
      restoreMfaContext: getActiveMfaChallengeContext(),
    });
  }

  async function handlePostMfaPasskeyEnrollment() {
    setPasskeyEnrollmentLoading(true);
    setPasskeyEnrollmentError('');

    try {
      await associateWebAuthnCredential();
      try {
        await setPasskeyMfaEnabled(true);
        toast.success('Passkey added and enabled.');
      } catch (mfaErr) {
        console.error('Failed to enable passkey MFA preference:', mfaErr);
        toast.error(`Passkey saved but could not enable as MFA: ${mfaErr.message || mfaErr.name}`);
      }
      setPromptPasskeyEnrollmentAfterMfa(false);
      setShowPasskeyEnrollmentPrompt(false);
      enterApp();
    } catch (err) {
      console.error('Post-MFA passkey enrollment error:', err);
      if (err.name === 'NotAllowedError') {
        setPasskeyEnrollmentError('Passkey registration was cancelled.');
      } else {
        setPasskeyEnrollmentError(err.message || 'Error registering passkey.');
      }
    } finally {
      setPasskeyEnrollmentLoading(false);
    }
  }

  function skipPostMfaPasskeyEnrollment() {
    setPromptPasskeyEnrollmentAfterMfa(false);
    setShowPasskeyEnrollmentPrompt(false);
    enterApp();
  }

  // Submit email MFA code
  async function handleEmailOtpCodeSubmit(event) {
    event.preventDefault();
    setIsLoading(true);
    setEmailOtpError('');

    try {
      const result = await confirmSignIn({ challengeResponse: emailOtpCode });

      if (result.nextStep.signInStep === 'DONE') {
        toast.success('Login successful!');
        await completeLogin();
      } else {
        setEmailOtpError('Verification could not be completed. Please sign in again.');
        setEmailOtpCode('');
      }
    } catch (error) {
      console.error('Email OTP verification error:', error);
      setEmailOtpError('Invalid code. Please try again.');
      setEmailOtpCode('');
      toast.error('Invalid code.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleVerifyAnotherWay() {
    setMfaRequired(false);
    setMfaCode('');
    setTotpRequired(false);
    setTotpCode('');
    setEmailOtpSent(false);
    setEmailOtpCode('');
    setEmailOtpError('');
    setMfaError('');
    setPasskeyError('');
    setMfaMethodSelection(true);
  }

  function resetMfaState({ clearPassword = true } = {}) {
    setMfaRequired(false);
    setMfaCode('');
    setPhoneSetupRequired(false);
    setPhoneNumber('');
    setPhoneVerificationSent(false);
    setPhoneVerificationCode('');
    setMfaError('');
    setMfaMethodSelection(false);
    setTotpRequired(false);
    setTotpCode('');
    setTotpSetupRequired(false);
    setTotpSetupUri('');
    setTotpSetupCode('');
    setRememberThisDevice(false);
    setEmailOtpSent(false);
    setEmailOtpCode('');
    setEmailOtpError('');
    setAvailableMfaMethods([]);
    setPasskeyError('');
    if (clearPassword) setPassword('');
  }

  const PasskeyInsteadButton = ({ helperText = true }) => (
    <div className="lg-field">
      <button
        type="button"
        onClick={handleUsePasskeyInstead}
        disabled={isLoading || !email}
        className="lg-option"
      >
        <div className="lg-option-icon">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
          </svg>
        </div>
        <div>
          <div className="lg-option-title">Use a passkey instead</div>
          <div className="lg-option-sub">Skip the code and verify with this device</div>
        </div>
      </button>
      {helperText && (
        <p className="lg-hint">
          Works when a passkey has been added to this account.
        </p>
      )}
      {passkeyError && (
        <div className="lg-alert lg-alert--note">
          {passkeyError}
        </div>
      )}
    </div>
  );

  // Ref for auto-focus
  const emailInputRef = React.useRef(null);

  React.useEffect(() => {
    document.body.classList.add("login-page");
    // Auto-focus on email input when page loads
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
    return function cleanup() {
      document.body.classList.remove("login-page");
    };
  }, []);

  // Load remember me preference
  React.useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Invite accepted: the password is set server-side; sign in with it through
  // login() so MFA setup etc. follow the usual path. A session left over from
  // someone else on this browser would otherwise win (UserAlreadyAuthenticated).
  const acceptInvite = async (inviteEmail, invitePassword) => {
    clearInviteToken();
    try { await signOut(); } catch (_) { /* no session */ }
    setEmail(inviteEmail);
    setPassword(invitePassword);
    setPendingInviteLogin(true);
  };
  const exitInvite = () => {
    clearInviteToken();
    setInvite(null);
    navigate('/login', { replace: true });
  };
  React.useEffect(() => {
    if (!pendingInviteLogin || !email || !password) return;
    setPendingInviteLogin(false);
    Promise.resolve(login({ preventDefault() {} })).finally(() => setInvite(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInviteLogin, email, password]);

  // The card title follows where the viewer is in the flow.
  const inviteStep = invite && !mfaRequired && !phoneSetupRequired && !mfaMethodSelection && !totpRequired && !totpSetupRequired && !emailOtpSent;
  const title = inviteStep
    ? (invite.phase === 'ready' ? `Welcome${invite.firstName ? `, ${invite.firstName}` : ' to Lodgeic'}` : invite.phase === 'checking' ? 'Welcome to Lodgeic' : 'Invite link')
    : forgotPasswordMode
    ? (resetPasswordStep === 'request' ? 'Reset your password' : resetPasswordStep === 'verify' ? (resetDelivery?.medium === 'SMS' ? 'Check your phone' : 'Check your email') : resetPasswordStep === 'invite' ? 'Check your email' : 'Choose a new password')
    : mfaMethodSelection ? 'Choose how to verify'
      : mfaRequired ? 'Enter the code we texted you'
        : totpRequired ? 'Enter your authenticator code'
          : totpSetupRequired ? 'Set up an authenticator app'
            : phoneSetupRequired ? (phoneVerificationSent ? 'Verify your phone' : 'Add a phone number')
              : emailOtpSent ? 'Check your email'
                : newPasswordRequired ? 'Set a new password'
                  : 'Log in to your account';

  return (
    <div className="lg-root">
      {/* The app sidebar, collapsed to its icon rail with only the brand mark.
          After sign-in the real shell mounts in this exact state and expands. */}
      <aside className="lg-rail" aria-label="Lodgeic">
        <div className="lg-rail-mark"><img src={brandMark} alt="Lodgeic" /></div>
      </aside>
      <main className="lg-card">
        <div className="lg-card-body">
          <div className="lg-form">
            <LoginBrand />
            <h1 className="lg-title">{title}</h1>

            <div>
              {/* Forgot Password Flow */}
              {forgotPasswordMode && (
                <>
                  {resetPasswordStep === 'request' ? (
                    <form onSubmit={handleForgotPasswordRequest} className="lg-stack">
                      <div className="lg-field">
                        <label htmlFor="forgot-email-input" className="lg-label">
                          Email
                        </label>
                        <div className="relative">
                          <input
                            id="forgot-email-input"
                            type="email"
                            className={`lg-input${emailError ? ' lg-input--invalid' : ''}`}
                            placeholder="name@company.com"
                            value={email}
                            onChange={handleEmailChange}
                            autoComplete="email"
                            aria-invalid={emailError ? 'true' : 'false'}
                          />
                                                  </div>
                        {emailError && (
                          <p className="lg-field-error">
                            {emailError}
                          </p>
                        )}
                      </div>
                      
                      {resetPasswordError && (
                        <div className="lg-alert lg-alert--error">
                          {resetPasswordError}
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={cancelForgotPassword}
                          className="lg-btn lg-btn--secondary flex-1"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading} // Disable button when loading
                          className="lg-btn lg-btn--primary flex-1"
                        >
                          {isLoading ? <Spinner /> : 'Send code'} {/* Show spinner or text */}
                        </button>
                      </div>
                    </form>
                  ) : resetPasswordStep === 'invite' ? (
                    // Never-activated invitee: a fresh setup link was mailed
                    // instead of a reset code (see handleForgotPasswordRequest).
                    <div className="lg-stack">
                      <p className="lg-lede lg-invite-lede">
                        You haven&rsquo;t finished setting up your account yet, so we&rsquo;ve emailed <strong>{email}</strong> a new setup link. It works once and expires in 72&nbsp;hours.
                      </p>
                      <button type="button" onClick={cancelForgotPassword} className="lg-btn lg-btn--secondary lg-btn--block">
                        Back to log in
                      </button>
                    </div>
                  ) : resetPasswordStep === 'verify' ? (
                    <form onSubmit={handleVerifyCode} className="lg-stack">
                      {resetDelivery?.destination ? (
                        <p className="lg-lede lg-invite-lede">
                          We {resetDelivery.medium === 'SMS' ? 'texted' : 'emailed'} a 6-digit code to <strong>{resetDelivery.destination}</strong>.
                        </p>
                      ) : null}
                      {/* Display the email in a disabled field */}
                      <div className="lg-field">
                        <label className="lg-label">
                          Email
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            className="lg-input"
                            value={email}
                            disabled
                          />
                        </div>
                      </div>
                      
                      <div className="lg-field">
                        <label className="lg-label">
                          Verification code
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="lg-input"
                            placeholder="6-digit code"
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                          />
                        </div>
                      </div>
                      
                      {resetPasswordError && (
                        <div className="lg-alert lg-alert--error">
                          {resetPasswordError}
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setResetPasswordStep('request')}
                          className="lg-btn lg-btn--secondary flex-1"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading} // Disable button when loading
                          className="lg-btn lg-btn--primary flex-1"
                        >
                          {isLoading ? <Spinner /> : 'Verify code'} {/* Show spinner or text */}
                        </button>
                      </div>
                      
                      <div className="lg-links">
                        <button 
                          type="button"
                          onClick={handleForgotPasswordRequest}
                          className="lg-link"
                        >
                          Resend code
                        </button>
                      </div>
                    </form>
                  ) : (
                    <form onSubmit={handleForgotPasswordConfirm} className="lg-stack">
                      <div className="lg-field">
                        <label htmlFor="reset-new-password" className="lg-label">
                          New password
                        </label>
                        <div className="relative">
                          <input
                            id="reset-new-password"
                            type={showNewPassword ? "text" : "password"}
                            className="lg-input lg-input--trail"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="lg-eye"
                            aria-label={showNewPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                          >
                            {showNewPassword ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                        {newPassword && (
                          <PasswordStrengthIndicator password={newPassword} />
                        )}
                      </div>

                      <div className="lg-field">
                        <label htmlFor="reset-confirm-password" className="lg-label">
                          Confirm password
                        </label>
                        <div className="relative">
                          <input
                            id="reset-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            className="lg-input lg-input--trail"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="lg-eye"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {resetPasswordError && (
                        <div className="lg-alert lg-alert--error">
                          {resetPasswordError}
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setResetPasswordStep('verify')}
                          className="lg-btn lg-btn--secondary flex-1"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading} // Disable button when loading
                          className="lg-btn lg-btn--primary flex-1"
                        >
                          {isLoading ? <Spinner /> : 'Reset password'} {/* Show spinner or text */}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
              
              {/* MFA Method Selection */}
              {!forgotPasswordMode && mfaMethodSelection && (
                <div className="lg-stack">
                  <p className="lg-lede">
                    {availableMfaMethods.length > 0 ? 'Choose a verification method.' : 'Choose how you\'d like to verify your identity.'}
                  </p>

                  {/* Only show methods the user has configured (from availableMfaMethods), or all if no info */}
                  {(availableMfaMethods.length === 0 || availableMfaMethods.includes('SMS')) && (
                    <button
                      onClick={() => handleMfaMethodSelect('SMS')}
                      disabled={isLoading}
                      className="lg-option"
                    >
                      <div className="lg-option-icon">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="lg-option-title">Text message</div>
                        <div className="lg-option-sub">Send a code to your phone</div>
                      </div>
                    </button>
                  )}

                  {(availableMfaMethods.length === 0 || availableMfaMethods.includes('TOTP')) && (
                    <button
                      onClick={() => handleMfaMethodSelect('TOTP')}
                      disabled={isLoading}
                      className="lg-option"
                    >
                      <div className="lg-option-icon">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div>
                        <div className="lg-option-title">Authenticator app</div>
                        <div className="lg-option-sub">Use Google Authenticator, Authy, etc.</div>
                      </div>
                    </button>
                  )}

                  {(availableMfaMethods.length === 0 || availableMfaMethods.includes('EMAIL')) && (
                    <button
                      onClick={() => handleMfaMethodSelect('EMAIL')}
                      disabled={isLoading}
                      className="lg-option"
                    >
                      <div className="lg-option-icon">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="lg-option-title">Email code</div>
                        <div className="lg-option-sub">Send a code to your email</div>
                      </div>
                    </button>
                  )}

                  <PasskeyInsteadButton />

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <div className="lg-links">
                    <button
                      type="button"
                      onClick={resetMfaState}
                      className="lg-link"
                    >
                      Back to log in
                    </button>
                  </div>
                </div>
              )}

              {/* TOTP Code Entry (during login) */}
              {!forgotPasswordMode && totpRequired && (
                <form ref={totpFormRef} onSubmit={handleTotpCodeSubmit} className="lg-stack">
                  <p className="lg-lede">
                    Enter the 6-digit code from your authenticator app.
                  </p>

                  <div className="lg-field">
                    <label className="lg-label">
                      Authenticator code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="lg-input lg-input--code"
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                    />
                  </div>

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <div className="lg-check">
                    <input
                      id="remember-device-totp"
                      type="checkbox"
                      checked={rememberThisDevice}
                      onChange={(e) => setRememberThisDevice(e.target.checked)}
                      className="lg-check-input"
                    />
                    <label htmlFor="remember-device-totp" className="lg-check-label">
                      Remember this device
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || totpCode.length < 6}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Verify'}
                  </button>

                  <PasskeyInsteadButton helperText={false} />

                  <div className="lg-links">
                    {availableMfaMethods.length > 1 && (
                      <>
                        <button type="button" onClick={handleVerifyAnotherWay}
                          className="lg-link">
                          Verify another way
                        </button>
                      </>
                    )}
                    <button type="button" onClick={resetMfaState}
                      className="lg-link">
                      Back to log in
                    </button>
                  </div>
                </form>
              )}

              {/* TOTP Setup — Scan QR Code */}
              {!forgotPasswordMode && totpSetupRequired && (
                <form onSubmit={handleTotpSetupVerify} className="lg-stack">
                  <p className="lg-lede">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code.
                  </p>

                  <div className="flex justify-center">
                    <div className="lg-qr-box">
                      <QRCodeSVG value={totpSetupUri} size={180} />
                    </div>
                  </div>

                  <div className="lg-field">
                    <label className="lg-label">
                      Code from the app
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="lg-input lg-input--code"
                      placeholder="000000"
                      value={totpSetupCode}
                      onChange={(e) => setTotpSetupCode(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || totpSetupCode.length < 6}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Verify and enable'}
                  </button>

                  <div className="lg-links">
                    <button
                      type="button"
                      onClick={resetMfaState}
                      className="lg-link"
                    >
                      Back to log in
                    </button>
                  </div>
                </form>
              )}

              {/* MFA Code Entry */}
              {!forgotPasswordMode && mfaRequired && (
                <form ref={mfaFormRef} onSubmit={handleMfaCodeSubmit} className="lg-stack">
                  <p className="lg-lede">
                    A verification code has been sent to your phone. Enter it below to continue.
                  </p>

                  <div className="lg-field">
                    <label className="lg-label">
                      Verification code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="lg-input lg-input--code"
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                    />
                  </div>

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <div className="lg-check">
                    <input
                      id="remember-device-sms"
                      type="checkbox"
                      checked={rememberThisDevice}
                      onChange={(e) => setRememberThisDevice(e.target.checked)}
                      className="lg-check-input"
                    />
                    <label htmlFor="remember-device-sms" className="lg-check-label">
                      Remember this device
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || mfaCode.length < 6}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Verify'}
                  </button>

                  <PasskeyInsteadButton helperText={false} />

                  <div className="lg-links">
                    <button type="button" onClick={() => handleResendLoginMfa('SMS')}
                      disabled={resendCooldown > 0 || isLoading}
                      className="lg-link">
                      {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend code'}
                    </button>
                    {availableMfaMethods.length > 1 && (
                      <>
                        <button type="button" onClick={handleVerifyAnotherWay}
                          className="lg-link">
                          Verify another way
                        </button>
                      </>
                    )}
                    <button type="button" onClick={resetMfaState}
                      className="lg-link">
                      Back to log in
                    </button>
                  </div>
                </form>
              )}

              {/* Phone Setup — Collect Phone Number */}
              {!forgotPasswordMode && phoneSetupRequired && !phoneVerificationSent && (
                <form onSubmit={handlePhoneSetup} className="lg-stack">
                  <p className="lg-lede">
                    Two-factor authentication is required. Please add your phone number to receive verification codes.
                  </p>

                  <div className="lg-field">
                    <label className="lg-label">
                      Phone number
                    </label>
                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      className="lg-input lg-phone"
                    />
                  </div>

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !phoneNumber}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Send code'}
                  </button>

                  <div className="lg-links">
                    <button
                      type="button"
                      onClick={resetMfaState}
                      className="lg-link"
                    >
                      Back to log in
                    </button>
                  </div>
                </form>
              )}

              {/* Phone Setup — Verify Phone Code */}
              {!forgotPasswordMode && phoneSetupRequired && phoneVerificationSent && (
                <form onSubmit={handlePhoneVerification} className="lg-stack">
                  <p className="lg-lede">
                    Enter the code sent to {phoneNumber ? phoneNumber.replace(/(\+\d{1,3})\d{4,}(\d{2})/, '$1 •••• ••$2') : 'your phone'}.
                  </p>

                  <div className="lg-field">
                    <label className="lg-label">
                      Verification code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="lg-input lg-input--code"
                      placeholder="000000"
                      value={phoneVerificationCode}
                      onChange={(e) => setPhoneVerificationCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                    />
                  </div>

                  {mfaError && (
                    <div className="lg-alert lg-alert--error">
                      {mfaError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || phoneVerificationCode.length < 6}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Verify and enable'}
                  </button>

                  <div className="lg-links">
                    <button
                      type="button"
                      onClick={handleResendPhoneCode}
                      disabled={resendCooldown > 0}
                      className="lg-link"
                    >
                      {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPhoneVerificationSent(false); setPhoneVerificationCode(''); setMfaError(''); }}
                      className="lg-link"
                    >
                      Use different number
                    </button>
                  </div>
                </form>
              )}

              {/* Email MFA — Enter code from email */}
              {!forgotPasswordMode && emailOtpSent && (
                <form ref={emailOtpFormRef} onSubmit={handleEmailOtpCodeSubmit} className="lg-stack">
                  <p className="lg-lede">
                    Enter the code we sent to <strong>{email}</strong>
                  </p>

                  <div className="lg-field">
                    <label className="lg-label">Verification code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      className="lg-input lg-input--code"
                      placeholder="Code"
                      value={emailOtpCode}
                      onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, ''))}
                      autoFocus
                    />
                  </div>

                  {emailOtpError && (
                    <div className="lg-alert lg-alert--error">
                      {emailOtpError}
                    </div>
                  )}

                  <div className="lg-check">
                    <input
                      id="remember-device-email"
                      type="checkbox"
                      checked={rememberThisDevice}
                      onChange={(e) => setRememberThisDevice(e.target.checked)}
                      className="lg-check-input"
                    />
                    <label htmlFor="remember-device-email" className="lg-check-label">
                      Remember this device
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || emailOtpCode.length < 4}
                    className="lg-btn lg-btn--primary lg-btn--block"
                  >
                    {isLoading ? <Spinner /> : 'Verify and sign in'}
                  </button>

                  <PasskeyInsteadButton helperText={false} />

                  <div className="lg-links">
                    <button type="button" onClick={() => handleResendLoginMfa('EMAIL')}
                      disabled={resendCooldown > 0 || isLoading}
                      className="lg-link">
                      {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend code'}
                    </button>
                    {availableMfaMethods.length > 1 && (
                      <>
                        <button type="button" onClick={handleVerifyAnotherWay}
                          className="lg-link">
                          Verify another way
                        </button>
                      </>
                    )}
                    <button type="button" onClick={resetMfaState}
                      className="lg-link">
                      Back to log in
                    </button>
                  </div>
                </form>
              )}

              {/* Welcome-email invite link */}
              {inviteStep && !forgotPasswordMode && (
                <InviteWelcome
                  token={inviteToken}
                  invite={invite}
                  renderStrength={(pw) => <PasswordStrengthIndicator password={pw} />}
                  onAccepted={acceptInvite}
                  onExit={exitInvite}
                />
              )}

              {/* Normal Login Flow */}
              {!inviteStep && !forgotPasswordMode && !mfaRequired && !phoneSetupRequired && !mfaMethodSelection && !totpRequired && !totpSetupRequired && !emailOtpSent && (
                <>
                  <form onSubmit={newPasswordRequired ? handleNewPasswordSubmit : login} className="lg-stack">
                    {/* Email Input */}
                    <div className="lg-field">
                      <label htmlFor="email-input" className="lg-label">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          id="email-input"
                          ref={emailInputRef}
                          type="email"
                          className={`lg-input${emailError ? ' lg-input--invalid' : ''}`}
                          placeholder="name@company.com"
                          value={email}
                          onChange={handleEmailChange}
                          aria-invalid={emailError ? 'true' : 'false'}
                          aria-describedby={emailError ? 'email-error' : undefined}
                          autoComplete="email"
                        />
                                              </div>
                      {emailError && (
                        <p id="email-error" className="lg-field-error">
                          {emailError}
                        </p>
                      )}
                    </div>

                    {/* Password Input */}
                    <div className="lg-field">
                      <div className="lg-label">
                        <label htmlFor="password-input">Password</label>
                        {!newPasswordRequired && (
                          <button type="button" onClick={() => setForgotPasswordMode(true)} className="lg-link">
                            Forgot password?
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          id="password-input"
                          type={showPassword ? "text" : "password"}
                          className="lg-input lg-input--trail"
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          aria-label="Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="lg-eye"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* New Password Fields */}
                    {newPasswordRequired && (
                      <>
                        <div className="lg-field">
                          <label htmlFor="new-password-input" className="lg-label">
                            New password
                          </label>
                          <div className="relative">
                            <input
                              id="new-password-input"
                              type={showNewPassword ? "text" : "password"}
                              className="lg-input lg-input--trail"
                              placeholder="Enter new password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              autoComplete="new-password"
                              aria-label="New Password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="lg-eye"
                              aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                              tabIndex={-1}
                            >
                              {showNewPassword ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {newPassword && (
                            <PasswordStrengthIndicator password={newPassword} />
                          )}
                        </div>
                        <div className="lg-field">
                          <label htmlFor="confirm-password-input" className="lg-label">
                            Confirm password
                          </label>
                          <div className="relative">
                            <input
                              id="confirm-password-input"
                              type={showConfirmPassword ? "text" : "password"}
                              className="lg-input lg-input--trail"
                              placeholder="Confirm new password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              autoComplete="new-password"
                              aria-label="Confirm Password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="lg-eye"
                              aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Error Message */}
                    {passwordError && (
                      <div className="lg-alert lg-alert--error">
                        {passwordError}
                      </div>
                    )}

                    {/* Remember Me Checkbox */}
                    {!newPasswordRequired && (
                      <div className="lg-check">
                        <input
                          id="remember-me"
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="lg-check-input"
                        />
                        <label htmlFor="remember-me" className="lg-check-label">
                          Remember me
                        </label>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading} // Disable button when loading
                      className="lg-btn lg-btn--primary lg-btn--block"
                    >
                      {isLoading ? <Spinner /> : (newPasswordRequired ? 'Set new password' : 'Log in')}
                    </button>
                  </form>

                  {/* Divider */}
                  <div className="lg-divider"><span>or</span></div>

                  {/* Passkey sign-in */}
                  <button
                    type="button"
                    onClick={() => handlePasskeySignIn()}
                    disabled={isLoading || !email}
                    className="lg-btn lg-btn--secondary lg-btn--block"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                    Use a passkey
                  </button>

                  {passkeyError && (
                    <div className="lg-alert lg-alert--note">
                      {passkeyError}
                    </div>
                  )}

                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {showPasskeyEnrollmentPrompt && (
        <div className="lg-scrim" role="dialog" aria-modal="true" aria-labelledby="passkey-prompt-title">
          <div className="lg-dialog">
            <div className="lg-option-icon">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
              </svg>
            </div>
            <h2 id="passkey-prompt-title">Add a passkey?</h2>
            <p>This account has no passkey yet. Add one now and future sign-ins can skip the code.</p>

            <div className="lg-stack" style={{ gap: 10 }}>
              {passkeyEnrollmentError && (
                <div className="lg-alert lg-alert--error">
                  {passkeyEnrollmentError}
                </div>
              )}
              <button
                type="button"
                onClick={handlePostMfaPasskeyEnrollment}
                disabled={passkeyEnrollmentLoading}
                className="lg-btn lg-btn--primary lg-btn--block"
              >
                {passkeyEnrollmentLoading ? <Spinner /> : 'Add passkey'}
              </button>
              <button
                type="button"
                onClick={skipPostMfaPasskeyEnrollment}
                disabled={passkeyEnrollmentLoading}
                className="lg-btn lg-btn--secondary lg-btn--block"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Password Strength Indicator Component
function PasswordStrengthIndicator({ password }) {
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: '#EF4444' };
    if (strength <= 3) return { strength, label: 'Fair', color: '#F59E0B' };
    if (strength <= 4) return { strength, label: 'Good', color: '#10B981' };
    return { strength, label: 'Strong', color: '#059669' };
  };

  const { strength, label, color } = getPasswordStrength(password);

  if (!password) return null;

  const rules = [
    ['At least 8 characters', password.length >= 8],
    ['Upper and lowercase letters', /[a-z]/.test(password) && /[A-Z]/.test(password)],
    ['A number', /\d/.test(password)],
    ['A symbol', /[^a-zA-Z0-9]/.test(password)],
  ];

  return (
    <div className="lg-strength">
      <div className="lg-strength-row">
        <span>Password strength</span>
        <span style={{ color, fontWeight: 500 }}>{label}</span>
      </div>
      <div className="lg-strength-bar">
        <div style={{ width: `${(strength / 5) * 100}%`, backgroundColor: color }} />
      </div>
      <ul className="lg-strength-rules">
        {rules.map(([text, met]) => (
          <li key={text} data-met={met ? 'true' : 'false'}>{met ? '✓' : '○'} {text}</li>
        ))}
      </ul>
    </div>
  );
}

// Simple Spinner Component
function Spinner() {
  return (
    <svg
      className="animate-spin lg-spinner"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

export default LoginPage;
